import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { addBreadcrumb } from '@/shared/lib/breadcrumbs';
import { captureMutationError } from '@/shared/lib/sentry-utils';
import { getAdaptiveInterval } from '@/shared/hooks/use-adaptive-interval';
import { parseApiError } from '../parse-error';
import { queryKeys, POLLING_INTERVALS } from '../query-keys';
import {
  getBots,
  getBot,
  getBotStatus,
  createBot,
  updateBot,
  deleteBot,
  startBot,
  stopBot,
  restartBot,
  haltBot,
  resumeBot,
  cancelAllOrders,
  getBotSignals,
  getBotRiskLog,
  getBotEvents,
  getBotResolvedConfig,
  previewBotConfig,
  getBotConfigHistory,
  getBotAnalytics,
  getBotExecutionQuality,
  getBotSummary,
  getBotDrift,
  getBotAttribution,
  rollbackBotConfig,
  type BotCreate,
  type BotUpdate,
  type BotListParams,
  type BotListResponse,
  type BotSublistParams,
  type BotStatusResponse,
  type ConfigPreviewRequest,
  type RollbackRequest,
  type AnalyticsWindow,
} from '../endpoints/bots';

export function useBots(params: BotListParams = {}) {
  return useQuery({
    queryKey: queryKeys.bots.list(params),
    queryFn: () => getBots(params),
    refetchInterval: POLLING_INTERVALS.botStatus,
    refetchIntervalInBackground: false,
    staleTime: POLLING_INTERVALS.botStatus,
  });
}

/**
 * LOK-1128: One canonical fleet fetch shared by all derivation call sites
 * (counts, top-N slices, full lists). Every subscriber reads from the same
 * cache key, so the entire app polls `/bots?limit=200` once per `botStatus`
 * window instead of fanning out to a separate request per `limit` arg.
 *
 * Pass a `select` to derive what your component actually needs — TanStack
 * Query memoises the projection per subscriber.
 */
const FLEET_PARAMS: BotListParams = { limit: 200 };

export function useBotsFleet<TSelected = BotListResponse>(
  select?: (data: BotListResponse) => TSelected,
) {
  return useQuery({
    queryKey: queryKeys.bots.list(FLEET_PARAMS),
    queryFn: () => getBots(FLEET_PARAMS),
    refetchInterval: POLLING_INTERVALS.botStatus,
    refetchIntervalInBackground: false,
    staleTime: POLLING_INTERVALS.botStatus,
    select,
  });
}

// Reserved slugs for UI-only routes (e.g. /bots/new, /bots/create) that must
// never leak into bot_id path params and trigger 404s on the backend.
const RESERVED_BOT_SLUGS = new Set(['', 'new', 'create']);

function isFetchableBotId(id: string | number): boolean {
  return !RESERVED_BOT_SLUGS.has(String(id));
}

export function useBot(id: string | number) {
  return useQuery({
    queryKey: queryKeys.bots.detail(String(id)),
    queryFn: () => getBot(id),
    staleTime: 30_000,
    enabled: isFetchableBotId(id),
  });
}

export function useBotStatus(id: string | number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.bots.status(String(id)),
    queryFn: () => getBotStatus(id),
    refetchInterval: (query) => getAdaptiveInterval(query.state.data?.runtime_state),
    refetchIntervalInBackground: false,
    staleTime: (query) => getAdaptiveInterval(query.state.data?.runtime_state),
    enabled: enabled && isFetchableBotId(id),
  });
}

export function useCreateBot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BotCreate) => createBot(body),
    onSuccess: (bot) => {
      addBreadcrumb('bot.action', 'Bot created', { botId: bot.id, name: bot.name });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.list() });
      void queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      toast.success(`Bot "${bot.name}" created`);
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to create bot'));
      captureMutationError(err, { mutation: 'create_bot' });
    },
  });
}

export function useUpdateBot(id: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BotUpdate) => updateBot(id, body),
    onSuccess: (bot) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.detail(String(id)) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.all() });
      toast.success(`Bot "${bot.name}" updated`);
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to update bot'));
      captureMutationError(err, { mutation: 'update_bot', bot_id: id });
    },
  });
}

export function useDeleteBot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => deleteBot(id),
    onSuccess: (_data, id) => {
      addBreadcrumb('bot.action', 'Bot deleted', { botId: id });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.list() });
      void queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      toast.success('Bot deleted');
    },
    onError: (err, id) => {
      toast.error(parseApiError(err, 'Failed to delete bot'));
      captureMutationError(err, { mutation: 'delete_bot', bot_id: id });
    },
  });
}

export function useStartBot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => startBot(id),
    onMutate: async (id) => {
      const key = queryKeys.bots.status(String(id));
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BotStatusResponse>(key);
      if (previous) {
        queryClient.setQueryData(key, { ...previous, runtime_state: 'starting' });
      }
      return { previous, key };
    },
    onSuccess: (_data, id) => {
      addBreadcrumb('bot.action', 'Bot started', { botId: id });
      toast.success('Bot starting…');
    },
    onError: (err, id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(parseApiError(err, 'Failed to start bot'));
      captureMutationError(err, { mutation: 'start_bot', bot_id: id });
    },
    onSettled: (_data, _err, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.status(String(id)) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.detail(String(id)) });
      void queryClient.invalidateQueries({ queryKey: ['portfolio'] });
    },
  });
}

export function useStopBot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => stopBot(id),
    onMutate: async (id) => {
      const key = queryKeys.bots.status(String(id));
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BotStatusResponse>(key);
      if (previous) {
        queryClient.setQueryData(key, { ...previous, runtime_state: 'stopping' });
      }
      return { previous, key };
    },
    onSuccess: (_data, id) => {
      addBreadcrumb('bot.action', 'Bot stopped', { botId: id });
      toast.success('Bot stopped');
    },
    onError: (err, id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(parseApiError(err, 'Failed to stop bot'));
      captureMutationError(err, { mutation: 'stop_bot', bot_id: id });
    },
    onSettled: (_data, _err, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.status(String(id)) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.detail(String(id)) });
      void queryClient.invalidateQueries({ queryKey: ['portfolio'] });
    },
  });
}

export function useRestartBot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => restartBot(id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.status(String(id)) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.all() });
      toast.success('Bot restarting…');
    },
    onError: (err, id) => {
      toast.error(parseApiError(err, 'Failed to restart bot'));
      captureMutationError(err, { mutation: 'restart_bot', bot_id: id });
    },
  });
}

export function useHaltBot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string | number; reason: string }) => haltBot(id, reason),
    onMutate: async ({ id }) => {
      const key = queryKeys.bots.status(String(id));
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BotStatusResponse>(key);
      if (previous) {
        queryClient.setQueryData(key, { ...previous, runtime_state: 'halted' });
      }
      return { previous, key };
    },
    onSuccess: (_data, { id }) => {
      addBreadcrumb('bot.action', 'Bot halted', { botId: id });
      toast.warning('Bot halted');
    },
    onError: (err, { id }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(parseApiError(err, 'Failed to halt bot'));
      captureMutationError(err, { mutation: 'halt_bot', bot_id: id });
    },
    onSettled: (_data, _err, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.status(String(id)) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.detail(String(id)) });
      void queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.system.health() });
    },
  });
}

export function useResumeBot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, token }: { id: string | number; token: string }) => resumeBot(id, token),
    onMutate: async ({ id }) => {
      const key = queryKeys.bots.status(String(id));
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BotStatusResponse>(key);
      if (previous) {
        queryClient.setQueryData(key, { ...previous, runtime_state: 'starting' });
      }
      return { previous, key };
    },
    onSuccess: () => {
      toast.success('Bot resumed');
    },
    onError: (err, { id }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(parseApiError(err, 'Failed to resume bot'));
      captureMutationError(err, { mutation: 'resume_bot', bot_id: id });
    },
    onSettled: (_data, _err, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.status(String(id)) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.detail(String(id)) });
    },
  });
}

export function useCancelAllOrders() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => cancelAllOrders(id),
    onSuccess: (result, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.orders(String(id)) });
      toast.success(`${result.cancelled_count} order(s) cancelled`);
    },
    onError: (err, id) => {
      toast.error(parseApiError(err, 'Failed to cancel orders'));
      captureMutationError(err, { mutation: 'cancel_all_orders', bot_id: id });
    },
  });
}

export function useBotSignals(id: string | number, params: BotSublistParams = {}) {
  return useQuery({
    queryKey: queryKeys.bots.signals(String(id), params),
    queryFn: () => getBotSignals(id, params),
    staleTime: 60_000,
    enabled: isFetchableBotId(id),
  });
}

export function useBotRiskLog(id: string | number, params: BotSublistParams = {}) {
  return useQuery({
    queryKey: queryKeys.bots.riskLog(String(id), params),
    queryFn: () => getBotRiskLog(id, params),
    staleTime: 60_000,
    enabled: isFetchableBotId(id),
  });
}

export function useBotEvents(id: string | number, params: BotSublistParams = {}) {
  return useQuery({
    queryKey: queryKeys.bots.events(String(id), params),
    queryFn: () => getBotEvents(id, params),
    staleTime: 60_000,
    enabled: isFetchableBotId(id),
  });
}

export function useBotResolvedConfig(id: string | number) {
  return useQuery({
    queryKey: queryKeys.bots.config(String(id)),
    queryFn: () => getBotResolvedConfig(id),
    staleTime: 5 * 60_000,
    enabled: isFetchableBotId(id),
  });
}

export function usePreviewBotConfig(id: string | number) {
  return useMutation({
    mutationFn: (body: ConfigPreviewRequest) => previewBotConfig(id, body),
  });
}

export function useBotConfigHistory(id: string | number, params: BotSublistParams = {}) {
  return useQuery({
    queryKey: queryKeys.bots.configHistory(String(id), params),
    queryFn: () => getBotConfigHistory(id, params),
    staleTime: 60_000,
    enabled: isFetchableBotId(id),
  });
}

export function useBotAnalytics(id: string | number, window: AnalyticsWindow = '30d') {
  return useQuery({
    queryKey: queryKeys.bots.analytics(String(id), window),
    queryFn: () => getBotAnalytics(id, window),
    staleTime: 60_000,
    enabled: isFetchableBotId(id),
  });
}

export function useBotExecutionQuality(id: string | number, window = 30) {
  return useQuery({
    queryKey: queryKeys.bots.executionQuality(String(id), window),
    queryFn: () => getBotExecutionQuality(id, window),
    staleTime: 60_000,
    enabled: isFetchableBotId(id),
  });
}

export function useBotSummary(id: string | number) {
  return useQuery({
    queryKey: queryKeys.bots.summary(String(id)),
    queryFn: () => getBotSummary(id),
    staleTime: 60_000,
    enabled: isFetchableBotId(id),
  });
}

export function useBotDrift(id: string | number, window: AnalyticsWindow = '30d') {
  return useQuery({
    queryKey: queryKeys.bots.drift(String(id), window),
    queryFn: () => getBotDrift(id, window),
    staleTime: 60_000,
    enabled: isFetchableBotId(id),
  });
}

export function useBotAttribution(id: string | number, window: AnalyticsWindow = '30d') {
  return useQuery({
    queryKey: queryKeys.bots.attribution(String(id), window),
    queryFn: () => getBotAttribution(id, window),
    staleTime: 60_000,
    enabled: isFetchableBotId(id),
  });
}

export function useRollbackBotConfig(id: string | number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RollbackRequest) => rollbackBotConfig(id, body),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.config(String(id)) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.configHistory(String(id)) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bots.detail(String(id)) });
      toast.success(`Config rolled back to version ${result.restored_version}`);
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to rollback config'));
      captureMutationError(err, { mutation: 'rollback_bot_config', bot_id: id });
    },
  });
}
